/* eslint-disable no-undef */
import { GraphQLClient } from 'graphql-request'
import { graphql } from 'msw'
import { IS_PROTOTYPE } from './prototype-mock/config'
import { mockResolve } from './prototype-mock/resolver'

// In prototype mode there is no backend, so the endpoint is irrelevant (every request is
// answered locally by mockResolve). Outside prototype mode it's still required.
const API_URL =
  import.meta.env.VITE_GRAPHQL_ENDPOINT ||
  (IS_PROTOTYPE ? 'http://prototype.local/graphql' : undefined)
if (!API_URL) {
  throw new Error('VITE_GRAPHQL_ENDPOINT environment variable is required')
}

let isGloballyRedirecting = false

class CircuitBreaker {
  constructor() {
    this.failures = new Map()
    this.FAILURE_THRESHOLD = 3
    this.RESET_TIMEOUT = 30000
  }

  recordFailure(url) {
    const now = Date.now()
    const existing = this.failures.get(url) || { count: 0, lastFailureTime: now }

    if (now - existing.lastFailureTime > this.RESET_TIMEOUT) {
      this.failures.set(url, { count: 1, lastFailureTime: now })
      return false
    }

    existing.count++
    existing.lastFailureTime = now
    this.failures.set(url, existing)

    return existing.count >= this.FAILURE_THRESHOLD
  }

  isOpen(url) {
    const failure = this.failures.get(url)
    if (!failure) {
      return false
    }

    const now = Date.now()
    if (failure.count >= this.FAILURE_THRESHOLD) {
      if (now - failure.lastFailureTime > this.RESET_TIMEOUT) {
        this.failures.delete(url)
        return false
      }
      return true
    }
    return false
  }

  reset(url) {
    this.failures.delete(url)
  }
}

const circuitBreaker = new CircuitBreaker()

class AuthenticatedGraphQLClient extends GraphQLClient {
  constructor(url, options) {
    super(url, options)
    this.isRedirecting = false
    this.firebaseAuth = null
    this.tokenRefreshPromise = null
    this.url = url
  }

  setFirebaseAuth(auth) {
    this.firebaseAuth = auth
  }

  async refreshTokenOnce() {
    if (this.tokenRefreshPromise) {
      return this.tokenRefreshPromise
    }

    this.tokenRefreshPromise = (async () => {
      try {
        if (this.firebaseAuth?.currentUser) {
          await this.firebaseAuth.currentUser.getIdToken(true)
          await new Promise((resolve) => setTimeout(resolve, 50))
        } else {
          await new Promise((resolve) => setTimeout(resolve, 100))
        }
      } finally {
        this.tokenRefreshPromise = null
      }
    })()

    return this.tokenRefreshPromise
  }

  async request(query, variables, requestHeaders) {
    // Prototype shell: every GraphQL call (GQty + legacy react-query) funnels through here.
    // Short-circuit to the local mock resolver so nothing ever hits a network.
    if (IS_PROTOTYPE) {
      return mockResolve(query, variables)
    }

    if (circuitBreaker.isOpen(this.url)) {
      const error = new Error('Service temporarily unavailable. Please try again in a moment.')
      error.code = 'CIRCUIT_OPEN'
      error.response = { status: 503 }
      throw error
    }

    try {
      const result = await super.request(query, variables, requestHeaders)
      circuitBreaker.reset(this.url)
      return result
    } catch (error) {
      const isServerError = error?.response?.status >= 500
      const isNetworkError =
        !error?.response || error.code === 'ECONNREFUSED' || error.code === 'ENOTFOUND'

      if (isServerError || isNetworkError) {
        const shouldOpen = circuitBreaker.recordFailure(this.url)
        if (shouldOpen) {
          console.error('🔴 Circuit breaker opened - too many failures. Stopping retries for 30s.')
        }
        throw error
      }

      if (this.isAuthError(error)) {
        if (this.isRecoverableUnauthorizedError(error)) {
          try {
            await this.refreshTokenOnce()
            const result = await super.request(query, variables, requestHeaders)
            circuitBreaker.reset(this.url)
            return result
          } catch (_retryError) {
            this.handleAuthFailure()
            throw error
          }
        } else {
          this.handleAuthFailure()
        }
      }
      throw error
    }
  }

  isRecoverableUnauthorizedError(error) {
    const status = error?.response?.status
    const responseError = (error?.response?.error || '').toLowerCase()

    // Only retry on 401 unauthorized (token might have just refreshed)
    // Don't retry on 403 forbidden or 404 user not found (not recoverable)
    return status === 401 || responseError === 'unauthorized'
  }

  isAuthError(error) {
    const status = error?.response?.status
    const responseError = (error?.response?.error || '').toLowerCase()
    const errorMessage = (error?.message || '').toLowerCase()

    if (status === 401 || status === 403 || responseError === 'unauthorized') {
      return true
    }

    if (status === 404 && (responseError.includes('user') || errorMessage.includes('user'))) {
      return true
    }

    if (
      error?.response?.errors &&
      error.response.errors.some(
        (e) =>
          e.extensions?.code === 'UNAUTHENTICATED' ||
          e.extensions?.code === 'FORBIDDEN' ||
          e.extensions?.code === 'USER_NOT_FOUND'
      )
    ) {
      return true
    }

    return false
  }

  handleAuthFailure() {
    if (isGloballyRedirecting || this.isRedirecting) {
      return
    }

    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname
      const isOnLoginPage = currentPath === '/login' || currentPath.includes('/login')
      const isOnSignupPage = currentPath === '/signup' || currentPath.startsWith('/signup/')

      if (isOnLoginPage || isOnSignupPage) {
        return
      }

      // Demo public pages should never trigger auth failure redirect.
      // The demo user's staff record may not exist yet during creation.
      const DEMO_PUBLIC_PATHS = ['/signup', '/verify', '/creating', '/magic-link']
      const isDemoPublicPage = DEMO_PUBLIC_PATHS.some(
        (p) => currentPath === p || currentPath.startsWith(p + '/')
      )
      if (isDemoPublicPage) {
        console.log(
          '[AuthClient] Skipping auth failure redirect — on demo public page:',
          currentPath
        )
        return
      }
    }

    isGloballyRedirecting = true
    this.isRedirecting = true

    if (typeof window !== 'undefined') {
      try {
        sessionStorage.clear()
      } catch {
        // Ignore sessionStorage errors
      }

      try {
        localStorage.removeItem('firebaseToken')
        localStorage.removeItem('authMechanism')
      } catch {
        // Ignore localStorage errors
      }

      if (this.firebaseAuth) {
        try {
          this.firebaseAuth.signOut()
        } catch {
          // Ignore Firebase signOut errors
        }
      }

      // On the demo subdomain, redirect to /signup instead of /login.
      // The user's StaffUser record may not exist (e.g. after data cleanup),
      // so sending them to login would just loop back here.
      const hostname = window.location.hostname
      const isDemoSubdomain =
        hostname.split('.')[0] === 'demo' ||
        ((hostname === 'localhost' || hostname === '127.0.0.1') &&
          import.meta.env.VITE_DEMO_STAFF_TENANT_ID)

      const redirectTo = isDemoSubdomain ? '/signup' : '/login'

      setTimeout(() => {
        window.location.replace(redirectTo)
      }, 100)
    }
  }
}

export const client = new AuthenticatedGraphQLClient(API_URL)

export const gqlQuery = ({ key, query, mockResponse = {} }) => {
  const mockIntegration = () => Promise.resolve(mockResponse)

  const mockHandler = !query
    ? mockIntegration
    : graphql.query(getRequestName(query), (req, res, context) => res(context.data(mockResponse)))

  return {
    key,
    query: !query
      ? mockIntegration
      : ({ meta: variables, queryKey, pageParam }) => {
          try {
            return client.request(query, {
              ...variables,
              ...queryKey?.[1],
              ...pageParam,
            })
          } catch (error) {
            if (process.env.NODE_ENV === 'development') {
              console.error('❌ GraphQL query error:', error)
            }
            throw error
          }
        },
    mockHandler,
    mockIntegration,
  }
}

export const gqlMutate = async ({
  mutation = null,
  variables,
  mockResponse = null,
  options = {},
}) => {
  if (!mutation) {
    return Promise.resolve(mockResponse || {})
  }

  const result = await client.request(mutation, variables)
  return result
}

export const mutationMockHandler = (mutation, mockResponse) =>
  graphql.mutation(getRequestName(mutation), (req, res, context) => res(context.data(mockResponse)))

export const getRequestName = (service) =>
  service?.match?.(/(?!^(mutation)\s)(\w+(?=\())/)?.[0] ||
  service?.match?.(/(?!^(query)\s)(\w+(?=\s\{))/)?.[0]
